import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Search,
  Calendar,
  ChevronDown,
  DollarSign,
  TrendingUp,
  ArrowDownToLine,
  Users,
  Percent,
  Download,
  RefreshCcw,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface CommissionRecord {
  id: string
  affiliateCode: string
  affiliateName: string
  period: string
  totalDeposit: number
  totalMembers: number
  activeMembers: number
  commissionRate: number
  grossCommission: number
  netCommission: number
  status: 'pending' | 'approved' | 'paid'
  createdAt: string
}

const mockCommissions: CommissionRecord[] = [
  {
    id: '1',
    affiliateCode: 'AFF001',
    affiliateName: 'สมชาย ใจดี',
    period: 'พ.ย. 2025',
    totalDeposit: 2850000,
    totalMembers: 125,
    activeMembers: 98,
    commissionRate: 45,
    grossCommission: 128250,
    netCommission: 125000,
    status: 'pending',
    createdAt: '2025-11-01',
  },
  {
    id: '2',
    affiliateCode: 'AFF002',
    affiliateName: 'วิชัย มั่งมี',
    period: 'พ.ย. 2025',
    totalDeposit: 1580000,
    totalMembers: 85,
    activeMembers: 72,
    commissionRate: 40,
    grossCommission: 63200,
    netCommission: 62000,
    status: 'approved',
    createdAt: '2025-11-01',
  },
  {
    id: '3',
    affiliateCode: 'AFF003',
    affiliateName: 'พิมพ์ใจ สวยงาม',
    period: 'พ.ย. 2025',
    totalDeposit: 920000,
    totalMembers: 45,
    activeMembers: 38,
    commissionRate: 35,
    grossCommission: 32200,
    netCommission: 31500,
    status: 'pending',
    createdAt: '2025-11-01',
  },
  {
    id: '4',
    affiliateCode: 'AFF001',
    affiliateName: 'สมชาย ใจดี',
    period: 'ต.ค. 2025',
    totalDeposit: 3200000,
    totalMembers: 142,
    activeMembers: 115,
    commissionRate: 45,
    grossCommission: 144000,
    netCommission: 142000,
    status: 'paid',
    createdAt: '2025-10-01',
  },
  {
    id: '5',
    affiliateCode: 'AFF002',
    affiliateName: 'วิชัย มั่งมี',
    period: 'ต.ค. 2025',
    totalDeposit: 1820000,
    totalMembers: 92,
    activeMembers: 78,
    commissionRate: 40,
    grossCommission: 72800,
    netCommission: 71500,
    status: 'paid',
    createdAt: '2025-10-01',
  },
]

const commissionTiers = [
  { level: 'Bronze', minDeposit: 0, maxDeposit: 500000, rate: 25, color: 'bg-orange-600' },
  { level: 'Silver', minDeposit: 500001, maxDeposit: 1500000, rate: 30, color: 'bg-gray-400' },
  { level: 'Gold', minDeposit: 1500001, maxDeposit: 3000000, rate: 35, color: 'bg-yellow-500' },
  { level: 'Platinum', minDeposit: 3000001, maxDeposit: 5000000, rate: 40, color: 'bg-cyan-500' },
  { level: 'VIP', minDeposit: 5000001, maxDeposit: Infinity, rate: 45, color: 'bg-purple-500' },
]

export function CommissionsPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPeriod, setSelectedPeriod] = useState('all')

  const filteredCommissions = useMemo(() => {
    let result = mockCommissions
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(c =>
        c.affiliateName.toLowerCase().includes(term) ||
        c.affiliateCode.toLowerCase().includes(term)
      )
    }
    if (selectedPeriod !== 'all') {
      result = result.filter(c => c.period === selectedPeriod)
    }
    return result
  }, [searchTerm, selectedPeriod])

  const stats = useMemo(() => ({
    totalGross: mockCommissions.reduce((sum, c) => sum + c.grossCommission, 0),
    totalNet: mockCommissions.reduce((sum, c) => sum + c.netCommission, 0),
    pending: mockCommissions.filter(c => c.status === 'pending').reduce((sum, c) => sum + c.netCommission, 0),
    paid: mockCommissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + c.netCommission, 0),
  }), [])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const formatNumber = (num: number) => new Intl.NumberFormat('th-TH').format(num)

  const getStatusBadge = (status: CommissionRecord['status']) => {
    const config = {
      pending: { label: 'รออนุมัติ', className: 'bg-warning/10 text-warning' },
      approved: { label: 'อนุมัติแล้ว', className: 'bg-blue-500/10 text-blue-500' },
      paid: { label: 'จ่ายแล้ว', className: 'bg-win/10 text-win' },
    }
    return <Badge variant="secondary" className={cn("border-0", config[status].className)}>{config[status].label}</Badge>
  }

  const periods = ['all', 'พ.ย. 2025', 'ต.ค. 2025', 'ก.ย. 2025']

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">ค่าคอมมิชชั่น</h1>
          <p className="text-muted-foreground">จัดการและตรวจสอบค่าคอมมิชชั่นพันธมิตร</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon">
            <RefreshCcw className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            ส่งออก
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-primary/10">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">คอมมิชชั่นรวม</p>
                <p className="text-xl font-bold">{formatCurrency(stats.totalNet)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-warning/10">
                <TrendingUp className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">รออนุมัติ</p>
                <p className="text-xl font-bold text-warning">{formatCurrency(stats.pending)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-win/10">
                <ArrowDownToLine className="h-5 w-5 text-win" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">จ่ายแล้ว</p>
                <p className="text-xl font-bold text-win">{formatCurrency(stats.paid)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-purple-500/10">
                <Percent className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">เฉลี่ยต่อคน</p>
                <p className="text-xl font-bold">{formatCurrency(stats.totalNet / mockCommissions.length)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commission Tiers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">อัตราค่าคอมมิชชั่นตามระดับ</CardTitle>
          <CardDescription>อัตราคอมมิชชั่นคำนวณจากยอดฝากรวมของสมาชิก</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-3">
            {commissionTiers.map((tier) => (
              <div key={tier.level} className="text-center p-3 rounded-lg bg-muted/30 border">
                <div className={cn("h-2 w-full rounded-full mb-2", tier.color)} />
                <p className="font-medium">{tier.level}</p>
                <p className="text-2xl font-bold text-primary">{tier.rate}%</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {tier.maxDeposit === Infinity 
                    ? `> ฿${formatNumber(tier.minDeposit)}`
                    : `฿${formatNumber(tier.minDeposit)} - ${formatNumber(tier.maxDeposit)}`
                  }
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Commission Table */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ค้นหา รหัส, ชื่อพันธมิตร..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Calendar className="h-4 w-4" />
                  {selectedPeriod === 'all' ? 'ทุกเดือน' : selectedPeriod}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {periods.map(p => (
                  <DropdownMenuItem key={p} onClick={() => setSelectedPeriod(p)}>
                    {p === 'all' ? 'ทุกเดือน' : p}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-y">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">พันธมิตร</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">งวด</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">ยอดฝากรวม</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">สมาชิก</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">อัตรา</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">คอมมิชชั่น</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">สถานะ</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {filteredCommissions.map((record) => (
                  <tr key={record.id} className="border-b hover:bg-muted/30">
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-medium">{record.affiliateName}</p>
                        <p className="text-xs text-muted-foreground">{record.affiliateCode}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Badge variant="outline">{record.period}</Badge>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-medium">{formatCurrency(record.totalDeposit)}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Users className="h-3 w-3 text-muted-foreground" />
                        <span>{record.activeMembers}/{record.totalMembers}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Badge variant="secondary" className="bg-primary/10 text-primary border-0">
                        {record.commissionRate}%
                      </Badge>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div>
                        <p className="font-semibold text-win">{formatCurrency(record.netCommission)}</p>
                        <p className="text-xs text-muted-foreground">
                          Gross: {formatCurrency(record.grossCommission)}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {getStatusBadge(record.status)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {record.status === 'pending' && (
                        <Button size="sm" variant="outline">อนุมัติ</Button>
                      )}
                      {record.status === 'approved' && (
                        <Button size="sm" className="bg-win hover:bg-win/90">จ่ายเงิน</Button>
                      )}
                      {record.status === 'paid' && (
                        <Button size="sm" variant="ghost" disabled>จ่ายแล้ว</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}










